import { change, getFormValues, InjectedFormProps, reduxForm } from "redux-form";
import { filter, find, map, orderBy, reduce, size } from "lodash";
import { useCallback, useEffect, useMemo } from "react";

import { BULK_ITEM_CATEGORY_OTHER_ID } from "src/constants";
import { BulkyItemsCategory, SchedulerWizardFormValues, TechnicalType } from "src/interfaces";
import { Button, ItemSelectionWrapper, Separator, TextDisplay, Wrapper } from "src/styled";
import { MultiSelect, TypedField } from "src/components";
import { setQuantityExceeded } from "src/redux/reducers";
import { translate } from "src/services";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import { WIZARD_FORM_NAME } from "./AddressForm";
import ItemSelectionQuantityRow from "./ItemSelectionQuantityRow";

interface PropsWithoutReduxForm {
  goToPreviousStep: () => void;
}

type Props = PropsWithoutReduxForm &
  InjectedFormProps<SchedulerWizardFormValues, PropsWithoutReduxForm>;

const ItemsSelectionForm = ({ handleSubmit, goToPreviousStep }: Props) => {
  const dispatch = useAppDispatch();

  const thisFormValues = useAppSelector(
    getFormValues(WIZARD_FORM_NAME)
  ) as SchedulerWizardFormValues;

  const maxItemsPerRequestReached = useAppSelector(
    (state) => state.bulkyItemsScheduler.errors.maxItemsPerRequestReached
  );
  const categoryTypes = useAppSelector((state) => state.categoryTypes.categoryTypes);
  const bulkyItemsTypes = useAppSelector((state) => state.bulkyItemsTypes.bulkyItemsTypes);
  const citySettings = useAppSelector((state) => state.citySettings.settings?.categories);
  const quantitySettings = useAppSelector(
    (state) => state.citySettings.settings?.vendorSettings?.maxItemsPerRequest
  );

  // Check if quantity exceeded for max quantity per request
  useEffect(() => {
    if (!quantitySettings) return;
    const totalItems = reduce(
      thisFormValues?.items,
      (result, item) => {
        return result + Number(item.quantity || 0);
      },
      0
    );
    if (!maxItemsPerRequestReached && totalItems > quantitySettings)
      dispatch(setQuantityExceeded(true));
    if (maxItemsPerRequestReached && totalItems <= quantitySettings)
      dispatch(setQuantityExceeded(false));
  }, [dispatch, maxItemsPerRequestReached, quantitySettings, thisFormValues?.items]);

  const bulkyItemsCategories = useMemo(() => {
    if (!categoryTypes || !bulkyItemsTypes || !citySettings) return [];
    const options = map(
      orderBy(
        filter(citySettings, (c) => !!size(c.items)),
        "bulkyCategoryTypeId"
      ),
      (category) => {
        const categoryLabel =
          find(categoryTypes, {
            id: category.bulkyCategoryTypeId,
          })?.name || "";

        const options = map(category.items, (item) => {
          const foundItem = find(bulkyItemsTypes, {
            id: item.bulkyItemTypeId,
          });
          const itemLabel = foundItem?.name || item.customBulkyItemName || "";
          return { label: itemLabel, value: item.bulkyItemTypeId || item.id };
        });
        return {
          label: categoryLabel,
          value: category.bulkyCategoryTypeId,
          options,
        };
      }
    );
    return options;
  }, [bulkyItemsTypes, categoryTypes, citySettings]);

  const onDropDownItemSelect = useCallback(
    (value: string[]) => {
      const selectedItems = reduce(
        map(value, (selected) => {
          const settingCategory = find(
            citySettings,
            (el) =>
              find(el.items, { bulkyItemTypeId: +selected }) || find(el.items, { id: +selected })
          ) as BulkyItemsCategory;

          const settingItem =
            find(settingCategory.items, {
              bulkyItemTypeId: +selected,
            }) || find(settingCategory.items, { id: +selected });

          const categoryType = find(categoryTypes, {
            id: settingCategory?.bulkyCategoryTypeId,
          });

          const itemType = find(bulkyItemsTypes, {
            id: settingItem?.bulkyItemTypeId,
          }) as TechnicalType;

          const itemInForm = find(thisFormValues?.items, {
            bulkyItemTypeId: +selected,
            bulkyCategoryTypeId: settingCategory?.bulkyCategoryTypeId,
          });

          if (itemInForm) return itemInForm;

          return {
            bulkyCategoryTypeId: settingCategory?.bulkyCategoryTypeId,
            bulkyItemTypeId: +selected,
            customBulkyItemName: null,
            quantity: 1,
            pickupDate: null,
            note: "",
            unassignedJobId: null,
            isCanceled: false,
            cancelDate: null,
            categoryLabelToDisplay: categoryType?.name,
            itemLabelToDisplay: itemType?.name || settingItem?.customBulkyItemName || "",
            message: settingItem?.message || settingCategory.message,
            price: settingItem?.price || settingCategory?.price,
            name: `items._${settingCategory?.bulkyCategoryTypeId}_${selected}`,
          };
        }),
        (result, item) => {
          return {
            ...result,
            [`_${item?.bulkyCategoryTypeId}_${item.bulkyItemTypeId}`]: item,
          };
        },
        {}
      );

      const otherItems = reduce(
        filter(Object.keys(thisFormValues?.items || {}), (itemKey) => {
          const item = thisFormValues?.items[itemKey];
          return item.bulkyCategoryTypeId === BULK_ITEM_CATEGORY_OTHER_ID;
        }),
        (result, itemKey) => {
          return {
            ...result,
            [itemKey]: thisFormValues?.items[itemKey],
          };
        },
        {}
      );

      dispatch(change(WIZARD_FORM_NAME, "items", { ...selectedItems, ...otherItems }));
    },
    [bulkyItemsTypes, categoryTypes, citySettings, dispatch, thisFormValues?.items]
  );

  // hide this for now
  // const onAddOtherItem = useCallback(() => {
  //   const otherCategory = find(categoryTypes, {
  //     id: BULK_ITEM_CATEGORY_OTHER_ID,
  //   });

  //   const selectedItems = thisFormValues?.items || [];
  //   // keep the thisFormValues?.items  bulkyCategoryTypeId: BULK_ITEM_CATEGORY_OTHER_ID
  //   const otherItemsLength = size(
  //     filter(thisFormValues?.items, (item) => {
  //       return item.bulkyCategoryTypeId === BULK_ITEM_CATEGORY_OTHER_ID;
  //     }) || []
  //   );

  //   const newItem = {
  //     bulkyCategoryTypeId: BULK_ITEM_CATEGORY_OTHER_ID,
  //     bulkyItemTypeId: null,
  //     customBulkyItemName: "",
  //     quantity: 1,
  //     pickupDate: null,
  //     note: "",
  //     unassignedJobId: null,
  //     isCanceled: false,
  //     cancelDate: null,
  //     categoryLabelToDisplay: otherCategory?.name,
  //     itemLabelToDisplay: "",
  //     name: `items._${BULK_ITEM_CATEGORY_OTHER_ID}_${otherItemsLength}`,
  //   };
  //   dispatch(
  //     change(WIZARD_FORM_NAME, "items", {
  //       ...selectedItems,
  //       [`_${BULK_ITEM_CATEGORY_OTHER_ID}_${otherItemsLength}`]: newItem,
  //     })
  //   );
  // }, [categoryTypes, dispatch, thisFormValues?.items]);

  const itemsToDisplay = useMemo(
    () =>
      map(Object.keys(thisFormValues?.items || {}), (item) => (
        <ItemSelectionQuantityRow item={thisFormValues?.items[item]} key={item} />
      )),
    [thisFormValues?.items, thisFormValues?.itemsMultiselect] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // hide this for now
  // const displayOtherButton = useMemo(() => {
  //   // if the other category is checked
  //   return find(citySettings, (el) => el.bulkyCategoryTypeId === BULK_ITEM_CATEGORY_OTHER_ID);
  // }, [citySettings]);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Separator height={50} />
      <TextDisplay margin="no no medium" block>
        <b>{translate("wizardPage.items")}</b>
      </TextDisplay>
      <TypedField
        component={MultiSelect}
        name="itemsMultiselect"
        props={{
          margin: "no auto",
          placeholder: translate("wizardPage.itemsPlaceholder"),
          isSearchable: true,
          canCheckAll: false,
          defaultToAll: false,
          fitContentWidth: true,
          minWidth: "250px",
          options: bulkyItemsCategories,
        }}
        onChange={(value: any) => onDropDownItemSelect(value)}
      />
      <Separator height={50} />
      <ItemSelectionWrapper>{itemsToDisplay}</ItemSelectionWrapper>
      <Separator height={20} />

      {/* hide this for now */}
      {/* {displayOtherButton && (
        <DottedButtonWrapper type="button" onClick={onAddOtherItem}>
          <TextDisplay color="primary">+{translate("wizardPage.addOtherNewItem")}</TextDisplay>
        </DottedButtonWrapper>
      )} */}

      <Separator height={50} />
      {maxItemsPerRequestReached && (
        <TextDisplay margin="no no medium" block color="alert">
          <em>
            {translate("alertMessages.quantityExceeded", {
              maxQuantity: quantitySettings,
            })}
          </em>
        </TextDisplay>
      )}
      <Wrapper display="flex" justifyContent="space-between">
        <Button
          type="button"
          color="primary"
          size="small"
          borderRadius="100px 0 0 100px"
          margin="no xSmall no no"
          line
          onClick={goToPreviousStep}
        >
          {translate("common.previousStep")}
        </Button>
        <Button
          type="submit"
          color="primary"
          size="medium"
          borderRadius="0 100px 100px 0"
          disabled={!size(thisFormValues?.items || {}) || maxItemsPerRequestReached}
        >
          {translate("common.nextStep")}
        </Button>
      </Wrapper>

      <Separator height={100} />
    </form>
  );
};

export default reduxForm<SchedulerWizardFormValues, PropsWithoutReduxForm>({
  form: WIZARD_FORM_NAME,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(ItemsSelectionForm);
